import { ACTION_COMMAND_SCHEMA } from '../actionCommands.js'
import { PORT_KIND } from './ports.js'

/**
 * Action node types. Each type maps 1:1 onto an existing Designer action type
 * (`actionType`), so a node's `data` keeps exactly that action's shape and the
 * existing action validation, editors and serializers apply unchanged.
 *
 * Action nodes only accept a trigger from an event node; they have no outputs,
 * which makes a direct UI-object → action wire structurally impossible.
 */
const ACTION_PORTS = Object.freeze({
    inputs: [{ id: 'trigger', label: 'Trigger', accepts: [PORT_KIND.TRIGGER] }],
    outputs: [],
})

const actionNode = (actionType, label, icon, color) => ({
    kind: 'action', actionType, label, icon, color, ports: ACTION_PORTS,
})

export const ACTION_NODE_TYPES = {
    command:            actionNode('command', 'Command', '⌘', '#0891b2'),
    setObjectProperty:  actionNode('setObjectProperty', 'Set property', '✏', '#7c3aed'),
    templateVisibility: actionNode('templateVisibility', 'Template', '⬡', '#7c3aed'),
    model3dLight:       actionNode('model3dLight', '3D light', '💡', '#a16207'),
    gpioEntity:         actionNode('gpioEntity', 'GPIO entity', '⏻', '#16a34a'),
}

const COMMAND_CATEGORY_STYLE = {
    Navigation: { icon: '▶', color: '#2563eb' },
    Display:    { icon: '☀', color: '#d97706' },
    Audio:      { icon: '🔊', color: '#0e7490' },
    System:     { icon: '↺', color: '#dc2626' },
}

/*
 * Action categories. The palette offers one node per category; the type within
 * the category (a "variant": node type + the preset that makes it that type)
 * is chosen in the node's properties. Command categories follow the command
 * catalogue, so a new command appears in its category automatically.
 */
const commandVariant = command => ({
    id: `command:${command.id}`,
    nodeType: 'command',
    label: command.label,
    preset: {
        commandId: command.id,
        parameters: Object.fromEntries((command.parameters ?? [])
            .map(parameter => [parameter.id, parameter.default ?? ''])),
    },
    ...(command.requiresVariables?.length ? { requiresVariables: [...command.requiresVariables] } : {}),
})

const nodeVariant = (nodeType, id, label, preset = {}) =>
    ({ id, nodeType, label: label ?? ACTION_NODE_TYPES[nodeType].label, preset })

const commandCategory = category => ({
    id: category.toLowerCase(),
    label: category,
    icon: COMMAND_CATEGORY_STYLE[category]?.icon ?? ACTION_NODE_TYPES.command.icon,
    color: COMMAND_CATEGORY_STYLE[category]?.color ?? ACTION_NODE_TYPES.command.color,
    commandCategory: category,
    variants: ACTION_COMMAND_SCHEMA.filter(command => command.category === category).map(commandVariant),
})

const KNOWN_COMMAND_CATEGORIES = Object.keys(COMMAND_CATEGORY_STYLE)

/** Action categories in display order, each with its variants. */
export function getActionCategories() {
    const otherCategories = [...new Set(ACTION_COMMAND_SCHEMA.map(command => command.category))]
        .filter(category => !KNOWN_COMMAND_CATEGORIES.includes(category))
    return [
        commandCategory('Navigation'),
        {
            id: 'object',
            label: 'Object',
            icon: ACTION_NODE_TYPES.setObjectProperty.icon,
            color: ACTION_NODE_TYPES.setObjectProperty.color,
            variants: [
                nodeVariant('setObjectProperty', 'setObjectProperty'),
                nodeVariant('templateVisibility', 'templateVisibility:show', 'Template → Show', { operation: 'show' }),
                nodeVariant('templateVisibility', 'templateVisibility:hide', 'Template → Hide', { operation: 'hide' }),
            ],
        },
        commandCategory('Display'),
        commandCategory('Audio'),
        commandCategory('System'),
        ...otherCategories.map(commandCategory),
        { id: '3d', label: '3D', icon: ACTION_NODE_TYPES.model3dLight.icon, color: ACTION_NODE_TYPES.model3dLight.color,
            variants: [nodeVariant('model3dLight', 'model3dLight')] },
        { id: 'gpio', label: 'GPIO', icon: ACTION_NODE_TYPES.gpioEntity.icon, color: ACTION_NODE_TYPES.gpioEntity.color,
            variants: [nodeVariant('gpioEntity', 'gpioEntity')] },
    ].filter(category => category.variants.length > 0)
}

export function getActionCategory(id) {
    return getActionCategories().find(category => category.id === id) ?? null
}

/** The category an action node belongs to, derived from its type (and command). */
export function getActionCategoryId(node) {
    if (node?.type === 'command') {
        const category = ACTION_COMMAND_SCHEMA.find(command => command.id === node.data?.commandId)?.category
        return category ? category.toLowerCase() : null
    }
    return getActionCategories().find(category =>
        category.variants.some(variant => variant.nodeType === node?.type))?.id ?? null
}

/** The variant an action node currently is (parameters do not count). */
export function getActionVariantId(node) {
    const category = getActionCategory(getActionCategoryId(node))
    return category?.variants.find(variant => variant.nodeType === node.type &&
        Object.entries(variant.preset).every(([key, value]) => key === 'parameters' || node.data?.[key] === value))?.id ?? null
}

/** Palette group with one node per action category; it starts as the category's first variant. */
export function getActionPaletteGroups() {
    return [{
        id: 'actions',
        label: 'Actions',
        color: ACTION_NODE_TYPES.command.color,
        entries: getActionCategories().map(category => {
            const [first] = category.variants
            return {
                id: `category:${category.id}`,
                category: category.id,
                nodeType: first.nodeType,
                label: category.label,
                icon: category.icon,
                color: category.color,
                preset: first.preset,
                title: `${category.label}: ${category.variants.map(variant => variant.label).join(', ')}`,
            }
        }),
    }]
}
